import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const NotificationSubscription = ({ incidentId, serviceName }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [preferences, setPreferences] = useState({
    statusUpdates: true,
    resolution: true,
    similarOutages: false,
    weeklyDigest: false
  });
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);

  const handleSubscribe = () => {
    if (email || phone) {
      setIsSubscribed(true);
      setShowSubscriptionForm(false);
      // Here you would typically make an API call to subscribe the user
    }
  };

  const handleUnsubscribe = () => {
    setIsSubscribed(false);
    // Here you would typically make an API call to unsubscribe the user
  };

  const handlePreferenceChange = (key, checked) => {
    setPreferences(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Bell" size={24} className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground mb-2">Stay Updated</h3>
          <p className="text-muted-foreground">
            Get notified about status changes and resolution updates for {serviceName}
          </p>
        </div>
      </div>
      {!isSubscribed ? (
        <div className="space-y-4">
          {!showSubscriptionForm ? (
            <div className="text-center py-4">
              <Button onClick={() => setShowSubscriptionForm(true)} className="w-full sm:w-auto">
                <Icon name="Bell" size={16} className="mr-2" />
                Subscribe to Updates
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Contact Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  type="email"
                  label="Email Address"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                />
                <Input
                  type="tel"
                  label="Phone Number (Optional)"
                  placeholder="+1 (555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e?.target?.value)}
                />
              </div>

              {/* Notification Preferences */}
              <div className="space-y-3">
                <h4 className="font-medium text-foreground">Notification Preferences</h4>
                
                <Checkbox
                  label="Status Updates"
                  description="Get notified when the incident status changes"
                  checked={preferences?.statusUpdates}
                  onChange={(e) => handlePreferenceChange('statusUpdates', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Resolution Notification"
                  description="Get notified when the incident is resolved"
                  checked={preferences?.resolution}
                  onChange={(e) => handlePreferenceChange('resolution', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Similar Outages"
                  description="Get notified about similar outages from this service"
                  checked={preferences?.similarOutages}
                  onChange={(e) => handlePreferenceChange('similarOutages', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Weekly Digest"
                  description="Receive a weekly summary of service reliability"
                  checked={preferences?.weeklyDigest}
                  onChange={(e) => handlePreferenceChange('weeklyDigest', e?.target?.checked)}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSubscribe}
                  disabled={!email && !phone}
                  className="flex-1 sm:flex-none"
                >
                  <Icon name="Check" size={16} className="mr-2" />
                  Subscribe
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowSubscriptionForm(false)}
                  className="flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Subscription Active */}
          <div className="flex items-center gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div className="flex-1">
              <p className="font-medium text-success">Subscription Active</p>
              <p className="text-sm text-success/80">
                You'll receive updates at {email || phone}
              </p>
            </div>
          </div>

          {/* Current Preferences */}
          <div className="space-y-3">
            <h4 className="font-medium text-foreground">Your Preferences</h4>
            <div className="space-y-2">
              {Object.entries(preferences)?.map(([key, value]) => (
                value && (
                  <div key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={14} className="text-success" />
                    <span className="capitalize">{key?.replace(/([A-Z])/g, ' $1')?.toLowerCase()}</span>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Management Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowSubscriptionForm(true)}
              size="sm"
            >
              <Icon name="Settings" size={14} className="mr-2" />
              Edit Preferences
            </Button>
            <Button
              variant="outline"
              onClick={handleUnsubscribe}
              size="sm"
            >
              <Icon name="BellOff" size={14} className="mr-2" />
              Unsubscribe
            </Button>
          </div>
        </div>
      )}
      {/* Social Sharing */}
      <div className="mt-8 pt-6 border-t border-border">
        <h4 className="font-medium text-foreground mb-4">Share This Outage</h4>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="sm">
            <Icon name="Twitter" size={14} className="mr-2" />
            Twitter
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Facebook" size={14} className="mr-2" />
            Facebook
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Linkedin" size={14} className="mr-2" />
            LinkedIn
          </Button>
          <Button variant="outline" size="sm">
            <Icon name="Copy" size={14} className="mr-2" />
            Copy Link
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSubscription;